import { derived, writable } from "svelte/store";
import { alerts } from "./alert";
import { slugify } from "$utils/strings";
import { apiReady } from "./auth";
import api from "$utils/api";

export interface Label {
  uuid: string;
  name: string;
  color: string;
  slug: string;
  teamOnly: boolean;
}

interface Loading {
  fetched: boolean;
}

export interface State extends Loading {
  communitySlug: string;
  labels: { [key: string]: Label };
}

const LabelStore = () => {
  const { subscribe, set, update } = writable<State>({
    communitySlug: "",
    labels: {},
    fetched: false,
  });

  return {
    subscribe,
    set,
    fetch: async (communitySlug: string) => {
      update((state) => ({ ...state, communitySlug, fetched: false }));
      await apiReady;
      const resp = await api.communitiesLabelsList({ communitySlug });
      const labels: { [key: string]: Label } = {};
      for (const label of (resp.data.results || []) as Label[]) {
        labels[label.uuid] = label;
      }
      update((state) => ({ ...state, labels, communitySlug, fetched: true }));
    },
    create: async (label: Label): Promise<Label | null> => {
      try {
        // create a unique uuid so we can use this label immediately
        label.uuid = `lb${crypto.randomUUID().slice(-11, -1)}`;
        label.slug = slugify(label.name);
        let communitySlug = "";
        // optimistic update
        update((state) => {
          state.labels[label.uuid] = label;
          communitySlug = state.communitySlug;
          return { ...state, labels: state.labels };
        });
        const resp = await api.communitiesLabelsCreate({
          communitySlug,
          labelSerialiser: label,
        });
        // update with server response
        update((state) => {
          state.labels[label.uuid] = resp.data as Label;
          return { ...state, labels: state.labels };
        });
        return label;
      } catch {
        // revert optimistic update
        update((state) => {
          delete state.labels[label.uuid];
          return {
            ...state,
            labels: state.labels,
          };
        });
        alerts.error(`Failed to create ${label.name} Label.`);
        return null;
      }
    },
    update: async (label: Label) => {
      let oldLabel = {} as Label;
      try {
        let communitySlug = "";
        // optimistic update
        update((state) => {
          oldLabel = state.labels[label.uuid];
          state.labels[label.uuid] = label;
          communitySlug = state.communitySlug;
          return { ...state, labels: state.labels };
        });
        const resp = await api.communitiesLabelsUpdate({
          communitySlug,
          uuid: label.uuid,
          labelSerialiser: label,
        });
        // update with server response
        update((state) => {
          state.labels[label.uuid] = resp.data as Label;
          return { ...state, labels: state.labels };
        });
        alerts.success(`${label.name} Label updated.`);
      } catch (e) {
        // revert optimistic update
        update((state) => {
          state.labels[label.uuid] = oldLabel;
          return {
            ...state,
            labels: state.labels,
          };
        });
        alerts.error(`Failed to update ${label.name} Label.`);
        throw e;
      }
    },
    delete: async (uuid: string) => {
      try {
        let communitySlug = "";
        update((state) => {
          communitySlug = state.communitySlug;
          delete state.labels[uuid];
          return {
            ...state,
            labels: state.labels,
          };
        });
        await api.communitiesLabelsDestroy({ communitySlug, uuid });
        alerts.success(`Label deleted.`);
      } catch (e) {
        alerts.error(`Failed to delete Label.`);
        throw e;
      }
    },
  };
};
export const labelStore = LabelStore();
export const { fetch, create, update, delete: destroy } = labelStore;
export const labels = derived(labelStore, ($labelStore) => $labelStore.labels);
export const labelsList = derived(labels, ($labels) => Object.values($labels));
export const labelsFetched = derived(labelStore, ($labelStore) => $labelStore.fetched);
export const uuids = derived(labelsList, ($labelsList) => {
  const uuids: string[] = [];
  for (const label of $labelsList) {
    uuids.push(label.uuid);
  }
  return uuids;
});
